import * as React from 'react';
import { useState, useCallback, useEffect } from 'react';
import styled from '@emotion/styled';
import { Collapse, Nav, Navbar, NavbarBrand, NavbarToggler, NavItem, NavLink } from 'reactstrap';
import { Link, NavLink as RRNavLink, useLocation } from 'react-router-dom';
import { HeaderProfileMenu } from './HeaderProfileMenu';
import { useTrans } from '../../Store/System/LocalizationStore';
import { observer } from 'mobx-react';
import { useRootStore } from '../../Store/useRootStore';
import { Theme } from '../../Theme/Theme';
import { LogActivityModal } from '../Home/LogActivityModal';
import { ExploreSearchOverlay } from '../../Components/Search';

const HeaderContainer = styled(Navbar)`
  background-color: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  min-height: 64px;
  position: sticky;
  top: var(--service-notice-height, 0px);
  z-index: 1000;

  @media (max-width: 768px) {
    min-height: 56px;
  }
`;

const HeaderInner = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;

  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  height: 40px;
`;

const Logo = styled.img`
  height: 100%;
  width: auto;
`;

const NavbarLink = styled(NavLink)`
  color: ${Theme.Colors.textColor};
  font-weight: 500;
  font-size: 14px;
  text-decoration: none;
  padding: 8px 16px !important;
  border-radius: 8px;
  transition: background-color 0.15s ease, color 0.15s ease;
  text-transform: capitalize;

  &:hover {
    background-color: ${Theme.Colors.lightestGrey};
    color: ${Theme.Colors.textColor};
  }

  &.active {
    color: ${Theme.Colors.primary} !important;
    background-color: ${Theme.Colors.primary}10;
  }

  @media (max-width: 767px) {
    font-size: 16px;
    padding: 14px 16px !important;
    border-radius: 12px;
    background: ${Theme.Colors.lightestGrey};

    &.active {
      background-color: ${Theme.Colors.primary}15;
    }
  }
`;

const CollapseContainer = styled(Collapse)`
  justify-content: center;

  @media (max-width: 767px) {
    position: fixed;
    top: calc(56px + var(--service-notice-height, 0px));
    left: 0;
    right: 0;
    bottom: 0;
    background: white;
    z-index: 999;
    padding: 16px;
    overflow-y: auto;
    display: flex !important;
    flex-direction: column;
    transform: translateX(-100%);
    transition: transform 0.3s ease;

    &.show {
      transform: translateX(0);
    }
  }
`;

const HeaderNav = styled(Nav)`
  flex: 1;
  justify-content: center;
  gap: 4px;

  @media (max-width: 767px) {
    flex-direction: column;
    gap: 8px;
    margin-bottom: 24px;
  }
`;

const NavItemStyled = styled(NavItem)`
  display: flex;
  align-items: center;
`;

const StyledNavbarToggler = styled(NavbarToggler)`
  border: none;
  padding: 8px;
  order: 3;

  &:focus {
    box-shadow: none;
  }

  @media (max-width: 767px) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 10px;

    &:hover {
      background: ${Theme.Colors.lightestGrey};
    }
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  border: none;
  background: transparent;
  color: ${Theme.Colors.textColor};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${Theme.Colors.lightestGrey};
  }

  i {
    font-size: 18px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const PrimaryActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  border: none;
  background: ${Theme.Colors.primary};
  color: white;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${Theme.Colors.primaryDark};
    transform: scale(1.05);
  }

  i {
    font-size: 18px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;


const RightActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  @media (max-width: 767px) {
    flex-direction: column;
    gap: 12px;
    width: 100%;
    padding-top: 16px;
    border-top: 1px solid ${Theme.Colors.lighterGrey};
  }
`;

const MobileRightActions = styled.div`
  display: none;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  margin-right: 8px;

  @media (max-width: 767px) {
    display: flex;
  }
`;

const MobileActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: none;
  background: transparent;
  color: ${Theme.Colors.textColor};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${Theme.Colors.lightestGrey};
  }

  i {
    font-size: 20px;
  }
`;

const MobilePrimaryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: none;
  background: ${Theme.Colors.primary};
  color: white;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${Theme.Colors.primaryDark};
  }

  i {
    font-size: 18px;
  }
`;

const MobileMenuButton = styled.button<{ $isOpen: boolean }>`
  display: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: none;
  background: ${({ $isOpen }) => ($isOpen ? Theme.Colors.lightestGrey : 'transparent')};
  color: ${Theme.Colors.textColor};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${Theme.Colors.lightestGrey};
  }

  i {
    font-size: 22px;
  }

  @media (max-width: 767px) {
    display: flex;
  }
`;

const Overlay = styled.div<{ $isOpen: boolean }>`
  display: none;
  position: fixed;
  top: calc(56px + var(--service-notice-height, 0px));
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 998;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
  transition: opacity 0.3s ease;

  @media (max-width: 767px) {
    display: block;
  }
`;

const MobileMenuContent = styled.div<{ $isOpen: boolean }>`
  display: none;
  position: fixed;
  top: calc(56px + var(--service-notice-height, 0px));
  left: 0;
  right: 0;
  bottom: 0;
  background: white;
  z-index: 999;
  padding: 20px;
  overflow-y: auto;
  transform: translateX(${({ $isOpen }) => ($isOpen ? '0' : '-100%')});
  transition: transform 0.3s ease;

  @media (max-width: 767px) {
    display: flex;
    flex-direction: column;
  }
`;

const MobileNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
`;

const MobileNavLink = styled(Link)<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-radius: 12px;
  text-decoration: none;
  font-size: 16px;
  font-weight: 600;
  color: ${({ $isActive }) => ($isActive ? Theme.Colors.primary : Theme.Colors.textColor)};
  background: ${({ $isActive }) => ($isActive ? `${Theme.Colors.primary}12` : Theme.Colors.lightestGrey)};
  transition: all 0.15s ease;

  &:hover {
    background: ${({ $isActive }) => ($isActive ? `${Theme.Colors.primary}18` : Theme.Colors.lighterGrey)};
    color: ${({ $isActive }) => ($isActive ? Theme.Colors.primary : Theme.Colors.textColor)};
  }

  i {
    font-size: 20px;
    width: 24px;
    text-align: center;
  }
`;

const MobileActionsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 20px;
  border-top: 1px solid ${Theme.Colors.lighterGrey};
  margin-top: auto;
`;

const MobileLogActivityButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px;
  border-radius: 12px;
  border: none;
  background: ${Theme.Colors.primary};
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${Theme.Colors.primaryDark};
  }

  i {
    font-size: 18px;
  }
`;

const MobileFeedbackLink = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  background: none;
  border: none;
  color: ${Theme.Colors.charcoal};
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: ${Theme.Colors.primary};
  }

  i {
    font-size: 14px;
  }
`;

export type HeaderProps = {};

const getLinkPrefixForPath = (pathname: string): string => {
  if (pathname.startsWith('/move')) return '/move/';
  if (pathname.startsWith('/mental-health')) return '/mental-health/';
  if (pathname.startsWith('/nutrition')) return '/nutrition/';
  if (pathname.startsWith('/academy')) return '/academy/';
  return '/academy/';
};

type CategorySearchConfig = {
  categoryFilter: string;
  categoryLabel: string;
};

const getCategorySearchConfig = (pathname: string): CategorySearchConfig | null => {
  // Library screens use category-filtered search
  if (pathname === '/move/library') {
    return { categoryFilter: 'movement', categoryLabel: 'Workouts' };
  }
  if (pathname === '/mental-health/library') {
    return { categoryFilter: 'mental', categoryLabel: 'Mental Health' };
  }
  if (pathname === '/academy/library') {
    return { categoryFilter: 'academy', categoryLabel: 'Academy' };
  }
  return null;
};

export const Header: React.FC<HeaderProps> = observer(() => {
  const { coachSubscription } = useRootStore();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [logActivityModalOpen, setLogActivityModalOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const { t } = useTrans();

  const handleToggle = useCallback(() => setIsOpen((p) => !p), []);
  const handleOpenLogActivity = useCallback(() => setLogActivityModalOpen(true), []);
  const handleCloseLogActivity = useCallback(() => setLogActivityModalOpen(false), []);
  const handleOpenSearch = useCallback(() => setIsSearchOpen(true), []);
  const handleCloseSearch = useCallback(() => setIsSearchOpen(false), []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const linkPrefix = getLinkPrefixForPath(location.pathname);
  const categorySearchConfig = getCategorySearchConfig(location.pathname);

  return (
    <HeaderContainer expand="md" container={false}>
      <HeaderInner>
        <NavbarBrand tag={Link} to="/home">
          <LogoContainer>
            <Logo src={require('../../Assets/Images/logo.png')} />
          </LogoContainer>
        </NavbarBrand>
        {/* Desktop Navigation */}
        <CollapseContainer className="d-none d-md-flex" navbar>
          <HeaderNav navbar>
            <NavItemStyled>
              <NavbarLink tag={RRNavLink} to="/home" activeClassName="active">
                {t('header.home', { defaultValue: 'Home' })}
              </NavbarLink>
            </NavItemStyled>
            {coachSubscription.canShowMove && (
              <NavItemStyled>
                <NavbarLink tag={RRNavLink} to="/move" activeClassName="active">
                  {t('header.moveLink', { defaultValue: 'Move' })}
                </NavbarLink>
              </NavItemStyled>
            )}
            {coachSubscription.canShowAllContent && (
              <NavItemStyled>
                <NavbarLink tag={RRNavLink} to="/mental-health" activeClassName="active">
                  {t('header.mentalHealth', { defaultValue: 'Mental Health' })}
                </NavbarLink>
              </NavItemStyled>
            )}
            {coachSubscription.canShowAllContent && (
              <NavItemStyled>
                <NavbarLink tag={RRNavLink} to="/nutrition" activeClassName="active">
                  {t('header.nutrition', { defaultValue: 'Nutrition' })}
                </NavbarLink>
              </NavItemStyled>
            )}
            {coachSubscription.canShowAcademy && (
              <NavItemStyled>
                <NavbarLink tag={RRNavLink} to="/academy" activeClassName="active">
                  {t('header.academy', { defaultValue: 'Academy' })}
                </NavbarLink>
              </NavItemStyled>
            )}
            <NavItemStyled>
              <NavbarLink tag={RRNavLink} to="/motivation" activeClassName="active">
                {t('header.motivation', { defaultValue: 'Motivation' })}
              </NavbarLink>
            </NavItemStyled>
          </HeaderNav>
          <RightActions>
            <ActionButton onClick={handleOpenSearch} title="Search (⌘K)">
              <i className="bi bi-search" />
            </ActionButton>
            <PrimaryActionButton onClick={handleOpenLogActivity} title="Log Activity">
              <i className="bi bi-plus-lg" />
            </PrimaryActionButton>
            <HeaderProfileMenu />
          </RightActions>
        </CollapseContainer>

        {/* Mobile Actions in Header */}
        <MobileRightActions>
          <MobileActionButton onClick={handleOpenSearch} aria-label="Search">
            <i className="bi bi-search" />
          </MobileActionButton>
          <MobilePrimaryButton onClick={handleOpenLogActivity} aria-label="Log Activity">
            <i className="bi bi-plus-lg" />
          </MobilePrimaryButton>
          <HeaderProfileMenu />
        </MobileRightActions>

        {/* Mobile Menu Toggle */}
        <MobileMenuButton $isOpen={isOpen} onClick={handleToggle} aria-label="Menu">
          <i className={isOpen ? 'bi bi-x-lg' : 'bi bi-list'} />
        </MobileMenuButton>

        {/* Mobile Menu Overlay */}
        <Overlay $isOpen={isOpen} onClick={handleToggle} />

        {/* Mobile Menu Content */}
        <MobileMenuContent $isOpen={isOpen}>
          <MobileNav>
            <MobileNavLink to="/home" $isActive={location.pathname === '/home' || location.pathname === '/'} onClick={handleToggle}>
              <i className="bi bi-house" />
              {t('header.home', { defaultValue: 'Home' })}
            </MobileNavLink>
            {coachSubscription.canShowMove && (
              <MobileNavLink to="/move" $isActive={location.pathname.startsWith('/move')} onClick={handleToggle}>
                <i className="bi bi-lightning-charge" />
                {t('header.moveLink', { defaultValue: 'Move' })}
              </MobileNavLink>
            )}
            {coachSubscription.canShowAllContent && (
              <MobileNavLink to="/mental-health" $isActive={location.pathname.startsWith('/mental-health')} onClick={handleToggle}>
                <i className="bi bi-heart-pulse" />
                {t('header.mentalHealth', { defaultValue: 'Mental Health' })}
              </MobileNavLink>
            )}
            {coachSubscription.canShowAllContent && (
              <MobileNavLink to="/nutrition" $isActive={location.pathname.startsWith('/nutrition')} onClick={handleToggle}>
                <i className="bi bi-egg-fried" />
                {t('header.nutrition', { defaultValue: 'Nutrition' })}
              </MobileNavLink>
            )}
            {coachSubscription.canShowAcademy && (
              <MobileNavLink to="/academy" $isActive={location.pathname.startsWith('/academy')} onClick={handleToggle}>
                <i className="bi bi-mortarboard" />
                {t('header.academy', { defaultValue: 'Academy' })}
              </MobileNavLink>
            )}
            <MobileNavLink to="/motivation" $isActive={location.pathname.startsWith('/motivation')} onClick={handleToggle}>
              <i className="bi bi-trophy" />
              {t('header.motivation', { defaultValue: 'Motivation' })}
            </MobileNavLink>
            <MobileNavLink to="/schedule" $isActive={location.pathname.startsWith('/schedule')} onClick={handleToggle}>
              <i className="bi bi-calendar-check" />
              {t('header.schedule', { defaultValue: 'Schedule' })}
            </MobileNavLink>
          </MobileNav>
          <MobileActionsSection>
            <MobileLogActivityButton onClick={() => { handleToggle(); handleOpenLogActivity(); }}>
              <i className="bi bi-plus-lg" />
              {t('header.logActivity', { defaultValue: 'Log Activity' })}
            </MobileLogActivityButton>
            <MobileFeedbackLink id="fw-custom-launcher" aria-label="Open support chat">
              <i className="bi bi-chat-dots" />
              {t('header.feedback', { defaultValue: 'Give Feedback' })}
            </MobileFeedbackLink>
          </MobileActionsSection>
        </MobileMenuContent>
      </HeaderInner>
      <LogActivityModal isOpen={logActivityModalOpen} onClose={handleCloseLogActivity} />
      <ExploreSearchOverlay
        isOpen={isSearchOpen}
        onClose={handleCloseSearch}
        linkPrefix={linkPrefix}
        categoryFilter={categorySearchConfig?.categoryFilter}
        categoryLabel={categorySearchConfig?.categoryLabel}
      />
    </HeaderContainer>
  );
});
